import { Button, Input } from '@bs/ui-components'
import { CheckCircle2, LoaderCircle } from 'lucide-react'
import { useEffect, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { AuthPageShell, FormError } from './auth-page-shell'
import {
  confirmTalentPasswordReset,
  verifyTalentPasswordResetCode,
} from '@/features/auth/api'

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const code = searchParams.get('oobCode') ?? ''
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [email, setEmail] = useState('')
  const [isVerifying, setIsVerifying] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {

    if (!code) {
      setError('This reset link is invalid or incomplete.')
      setIsVerifying(false)
      return
    }

    void verifyTalentPasswordResetCode(code)
      .then((result) => setEmail(result.email))
      .catch(() => setError('This reset link is invalid or has expired.'))
      .finally(() => setIsVerifying(false))
  }, [code])

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (password.length < 8) {
      setError('Password must contain at least 8 characters.')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setIsSubmitting(true)
    setError(null)
    try {
      await confirmTalentPasswordReset(code, password)
      setIsComplete(true)
    } catch {
      setError('Unable to reset the password. Request a new reset link and try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthPageShell>
      <form onSubmit={submit} className="w-full max-w-[500px] text-[#404040]">
        <h1 className="text-center text-[30px] font-medium">Reset Password</h1>

        {isVerifying ? (
          <div className="mt-10 flex justify-center" role="status">
            <LoaderCircle className="size-8 animate-spin text-primary" />
            <span className="sr-only">Verifying reset link</span>
          </div>
        ) : isComplete ? (
          <div className="mt-8 text-center">
            <CheckCircle2 className="mx-auto size-14 text-accent" />
            <p className="mt-6">Your password has been updated successfully.</p>
            <Button asChild variant="accent" size="lg" className="mt-8 h-[50px] w-full text-lg">
              <Link to="/login">Continue to login</Link>
            </Button>
          </div>
        ) : (
          <>
            {email ? <p className="mt-4 text-center text-[#8a8a8a]">Choose a new password for {email}.</p> : null}
            {error ? <FormError message={error} className="mt-7" /> : null}
            {email ? (
              <>
                <div className="mt-8">
                  <label htmlFor="new-password" className="mb-4 block">New password</label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="Enter password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className="h-11 rounded-xl px-4"
                    required
                    minLength={8}
                  />
                </div>
                <div className="mt-6">
                  <label htmlFor="confirm-password" className="mb-4 block">Confirm password</label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="Enter confirm password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    className="h-11 rounded-xl px-4"
                    required
                    minLength={8}
                  />
                </div>
                <Button
                  type="submit"
                  variant="accent"
                  size="lg"
                  className="mt-8 h-[50px] w-full text-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <LoaderCircle className="size-5 animate-spin" /> : null}
                  {isSubmitting ? 'Updating…' : 'Update password'}
                </Button>
              </>
            ) : (
              <p className="mt-8 text-center">
                <Link to="/forgot-password" className="text-primary hover:underline">Request a new link</Link>
              </p>
            )}
          </>
        )}
      </form>
    </AuthPageShell>
  )
}
